import { Fragment, useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import '../styles/generateoffer.css';

const ALL_OFFER_TYPES = ['PAID', 'UNPAID'];

// Which offer types the logged-in user may generate for a company.
// Admins can use both. This only controls what's shown — the backend
// checks the same rule again on every generate/send request.
function allowedTypesFor(user, isAdmin, companyId) {
  if (isAdmin) return ALL_OFFER_TYPES;
  const perm = (user?.permissions || []).find((p) => (p.company?._id || p.company) === companyId);
  return perm ? ALL_OFFER_TYPES.filter((t) => perm.offerTypes.includes(t)) : [];
}

function fmtDate(d) {
  if (!d) return 'MM/DD/YYYY';
  const dt = new Date(`${d}T00:00:00`);
  return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
}

// Ordinal day suffix: 1st, 2nd, 3rd, 4th ... 21st, 22nd, 23rd ...
function ordinal(n) {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`;
}

// Matches the target format exactly: "June 23rd 2026" — no comma, ordinal day.
function fmtLong(d) {
  if (!d) return '[Date]';
  const dt = new Date(`${d}T00:00:00`);
  const month = dt.toLocaleDateString('en-US', { month: 'long' });
  return `${month} ${ordinal(dt.getDate())} ${dt.getFullYear()}`;
}

const todayISO = () => new Date().toISOString().split('T')[0];
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());
const isPositiveNumber = (v) => v !== '' && v !== null && v !== undefined && !Number.isNaN(Number(v)) && Number(v) > 0;

export default function GenerateOfferPage() {
  const { admin, isAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const [resps, setResps] = useState(['']);
  const [toast, setToast] = useState(null);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const [form, setForm] = useState({
    offerType: 'PAID',
    candidateName: '',
    candidateAddress: '',
    candidateEmail: '',
    jobTitle: '',
    payRate: '',
    hoursPerWeek: 25,
    workHours: '9:00 AM to 2:00 PM, Monday to Friday',
    startDate: '',
    offerDate: todayISO(),
    workMode: 'a hybrid work model',
  });

  useEffect(() => {
    api.get('/companies').then((res) => {
      setCompanies(res.data.companies);
      if (res.data.companies.length) setCompanyId(res.data.companies[0]._id);
    }).finally(() => setCompaniesLoaded(true));
  }, []);

  const allowedTypes = allowedTypesFor(admin, isAdmin, companyId);

  // When the selected company changes, make sure the chosen offer type is
  // one this user is allowed for that company; if not, switch to one that is.
  useEffect(() => {
    if (allowedTypes.length && !allowedTypes.includes(form.offerType)) {
      setForm((f) => ({ ...f, offerType: allowedTypes[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, allowedTypes.join(',')]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (fieldErrors[k]) setFieldErrors((fe) => ({ ...fe, [k]: undefined }));
  };
  const company = companies.find((c) => c._id === companyId);

  // Salutation uses first name only ("Dear Pavan,") while every other field
  // (address block, signature) still uses the full candidateName untouched.
  const firstName = (form.candidateName || '').trim().split(/\s+/)[0] || '';

  const addResp = () => setResps((r) => [...r, '']);
  const removeResp = (i) => setResps((r) => r.filter((_, idx) => idx !== i));
  const updateResp = (i, val) => {
    setResps((r) => r.map((x, idx) => (idx === i ? val : x)));
    if (fieldErrors.responsibilities) setFieldErrors((fe) => ({ ...fe, responsibilities: undefined }));
  };

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const isUnpaid = form.offerType === 'UNPAID';

  // Full-form validation, run before every Download/Send. Checks every
  // field the selected offer type actually depends on — not just name and
  // email — so mistakes get caught before a PDF gets generated or an email
  // goes out to a candidate.
  const validateForm = () => {
    const errs = {};
    if (!companyId) errs.companyId = 'Select a company first.';
    else if (!allowedTypes.includes(form.offerType)) errs.companyId = "You don't have access to this offer type for this company.";
    if (!form.candidateName?.trim()) errs.candidateName = 'Candidate name is required.';
    if (!form.candidateEmail?.trim()) errs.candidateEmail = 'Candidate email is required.';
    else if (!isValidEmail(form.candidateEmail)) errs.candidateEmail = 'Enter a valid email address.';
    if (!isUnpaid && !form.candidateAddress?.trim()) errs.candidateAddress = 'Candidate address is required.';
    if (!form.jobTitle?.trim()) errs.jobTitle = 'Job title is required.';
    if (!form.startDate) errs.startDate = 'Start date is required.';
    if (!form.offerDate) errs.offerDate = 'Offer letter date is required.';
    if (isUnpaid) {
      if (!isPositiveNumber(form.hoursPerWeek)) errs.hoursPerWeek = 'Enter hours per week greater than 0.';
      else if (Number(form.hoursPerWeek) > 40) errs.hoursPerWeek = 'Hours per week cannot exceed 40.';
      if (!form.workHours?.trim()) errs.workHours = 'Work hours are required.';
    } else {
      if (!isPositiveNumber(form.payRate)) errs.payRate = 'Enter a pay rate greater than 0.';
    }
    if (!resps.some((r) => r.trim())) errs.responsibilities = 'Add at least one responsibility.';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildPayload = () => ({
    companyId,
    offerType: form.offerType,
    candidateName: form.candidateName,
    candidateAddress: form.candidateAddress,
    candidateEmail: form.candidateEmail,
    jobTitle: form.jobTitle,
    ...(isUnpaid
      ? { hoursPerWeek: form.hoursPerWeek, workHours: form.workHours }
      : { payRate: form.payRate, workMode: form.workMode }),
    startDate: form.startDate,
    offerDate: form.offerDate,
    responsibilities: resps.filter((r) => r.trim()),
  });

  const handleDownload = async () => {
    if (!validateForm()) return showToast('Please fix the highlighted fields before continuing', 'err');
    setDownloading(true);
    try {
      const res = await api.post('/offers/generate', buildPayload(), { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Offer_${form.candidateName.replace(/ /g, '_')}_${company.shortName.replace(/ /g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('PDF downloaded!', 'ok');
    } catch (err) {
      showToast(err.response?.data?.message || 'Error generating PDF', 'err');
    } finally {
      setDownloading(false);
    }
  };

  const handleSend = async () => {
    if (!validateForm()) return showToast('Please fix the highlighted fields before continuing', 'err');
    setSending(true);
    try {
      const res = await api.post('/offers/send', buildPayload());
      showToast(res.data.message, 'ok');
    } catch (err) {
      showToast(err.response?.data?.message || 'Error sending offer', 'err');
    } finally {
      setSending(false);
    }
  };

  const filledResps = resps.filter((r) => r.trim());
  const respItems = (filledResps.length ? filledResps : ['<Responsibilities>']).map((r, i) => <li key={i}>{r}</li>);

  // Renders a possibly multi-line address ("street\napt\ncity, state zip")
  // as separate visual lines, matching the target's mailing-address block.
  const addressLines = (form.candidateAddress || '<Current Address>')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  return (
    <div className="hpg-page">
      <div className="hpg-topbar">
        <h1 className="hpg-title">Generate Offer Letter</h1>
        <div className="hpg-live-badge">
          <span className="hpg-live-dot" aria-hidden="true" />
          Live Preview
        </div>
      </div>

      <div className="hpg-layout">
        {/* ── LEFT: form ── */}
        <div className="hpg-card">
          <div className="hpg-section-label hpg-section-label--first">Company</div>
          <div className="hpg-field">
            <select className="hpg-input" value={companyId} onChange={(e) => { setCompanyId(e.target.value); if (fieldErrors.companyId) setFieldErrors((fe) => ({ ...fe, companyId: undefined })); }}>
              <option value="" disabled hidden>
                Select a company
              </option>
              {companies.map((c) => (
                <option key={c._id} value={c._id}>{c.shortName}</option>
              ))}
            </select>
            {fieldErrors.companyId && <small className="hpg-field-err">{fieldErrors.companyId}</small>}
            {companiesLoaded && companies.length === 0 && (
              <small className="hpg-field-err">
                {isAdmin
                  ? 'No companies yet. Add one on the Companies page first.'
                  : "You don't have access to any company yet. Ask your admin to give you access."}
              </small>
            )}
          </div>

          <div className="hpg-section-label">Offer Type</div>
          <div className="hpg-offer-type-toggle">
            {allowedTypes.includes('PAID') && (
              <button
                type="button"
                className={`hpg-offer-type-btn ${!isUnpaid ? 'hpg-offer-type-btn--active' : ''}`}
                onClick={() => set('offerType', 'PAID')}
              >
                Paid
              </button>
            )}
            {allowedTypes.includes('UNPAID') && (
              <button
                type="button"
                className={`hpg-offer-type-btn ${isUnpaid ? 'hpg-offer-type-btn--active' : ''}`}
                onClick={() => set('offerType', 'UNPAID')}
              >
                Unpaid Internship
              </button>
            )}
          </div>

          <div className="hpg-section-label">Candidate Info</div>
          <div className="hpg-field">
            <label className="hpg-label">Full Name</label>
            <input className="hpg-input" value={form.candidateName} onChange={(e) => set('candidateName', e.target.value)} placeholder="Pavan kumar Bala" />
            {fieldErrors.candidateName && <small className="hpg-field-err">{fieldErrors.candidateName}</small>}
          </div>
          <div className="hpg-field">
            <label className="hpg-label">Current Address</label>
            {/* Textarea (not a single-line input) so a candidate's address can
                span multiple lines — street / apt+city / state+zip — exactly
                like the target letter's mailing-address block. Each line the
                user types becomes its own line in the preview and the docx. */}
            <textarea
              className="hpg-input"
              rows={3}
              style={{ resize: 'vertical', minHeight: 64, fontFamily: 'inherit' }}
              value={form.candidateAddress}
              onChange={(e) => set('candidateAddress', e.target.value)}
              placeholder={'700 E Drake Rd\nApt E7 Fort Collins,\nCO, 80525'}
            />
            {fieldErrors.candidateAddress && <small className="hpg-field-err">{fieldErrors.candidateAddress}</small>}
          </div>
          <div className="hpg-field">
            <label className="hpg-label">Candidate Email</label>
            <input className="hpg-input" type="email" value={form.candidateEmail} onChange={(e) => set('candidateEmail', e.target.value)} placeholder="john@email.com" />
            {fieldErrors.candidateEmail && <small className="hpg-field-err">{fieldErrors.candidateEmail}</small>}
          </div>

          <div className="hpg-section-label">Role Details</div>
          <div className="hpg-field">
            <label className="hpg-label">Job Title</label>
            <input className="hpg-input" value={form.jobTitle} onChange={(e) => set('jobTitle', e.target.value)} placeholder="Full Stack Engineer" />
            {fieldErrors.jobTitle && <small className="hpg-field-err">{fieldErrors.jobTitle}</small>}
          </div>
          <div className="hpg-row2">
            {isUnpaid ? (
              <div className="hpg-field">
                <label className="hpg-label">Hours per Week</label>
                <input
                  className="hpg-input"
                  type="number"
                  min="1"
                  max="40"
                  value={form.hoursPerWeek}
                  onChange={(e) => set('hoursPerWeek', e.target.value)}
                  placeholder="25"
                />
                {fieldErrors.hoursPerWeek && <small className="hpg-field-err">{fieldErrors.hoursPerWeek}</small>}
              </div>
            ) : (
              <div className="hpg-field">
                <label className="hpg-label">Pay Rate (USD/hr)</label>
                <div className="hpg-input-affix">
                  <span className="hpg-input-affix-symbol">$</span>
                  <input className="hpg-input hpg-input--affixed" value={form.payRate} onChange={(e) => set('payRate', e.target.value)} placeholder="25.00" />
                </div>
                {fieldErrors.payRate && <small className="hpg-field-err">{fieldErrors.payRate}</small>}
              </div>
            )}
            <div className="hpg-field">
              <label className="hpg-label">Start Date</label>
              <input className="hpg-input" type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
              {fieldErrors.startDate && <small className="hpg-field-err">{fieldErrors.startDate}</small>}
            </div>
          </div>
          {isUnpaid ? (
            <div className="hpg-field">
              <label className="hpg-label">Work Hours</label>
              <input
                className="hpg-input"
                value={form.workHours}
                onChange={(e) => set('workHours', e.target.value)}
                placeholder="9:00 AM to 2:00 PM, Monday to Friday"
              />
              {fieldErrors.workHours && <small className="hpg-field-err">{fieldErrors.workHours}</small>}
            </div>
          ) : (
            <div className="hpg-field">
              <label className="hpg-label">Work Mode</label>
              <select className="hpg-input" value={form.workMode} onChange={(e) => set('workMode', e.target.value)}>
                <option value="a hybrid work model">Hybrid</option>
                <option value="a remote work model">Remote</option>
                <option value="an onsite work model">Onsite</option>
                <option value="a hybrid/remote work model">Remote / Hybrid</option>
              </select>
            </div>
          )}
          <div className="hpg-field">
            <label className="hpg-label">Offer Letter Date</label>
            <input className="hpg-input" type="date" value={form.offerDate} onChange={(e) => set('offerDate', e.target.value)} />
            {fieldErrors.offerDate && <small className="hpg-field-err">{fieldErrors.offerDate}</small>}
          </div>

          <div className="hpg-section-label">Responsibilities</div>
          {fieldErrors.responsibilities && <small className="hpg-field-err" style={{ display: 'block', marginBottom: 8 }}>{fieldErrors.responsibilities}</small>}
          {resps.map((r, i) => (
            <div className="hpg-resp-row" key={i}>
              <input className="hpg-input" value={r} onChange={(e) => updateResp(i, e.target.value)} placeholder={`Responsibility ${i + 1}`} />
              <button type="button" className="hpg-btn-remove" onClick={() => removeResp(i)} aria-label="Remove responsibility">
                <CloseIcon />
              </button>
            </div>
          ))}
          <button type="button" className="hpg-btn-add" onClick={addResp}>
            <PlusIcon /> Add Responsibility
          </button>

          <div className="hpg-actions">
            <button className="hpg-btn hpg-btn--outline" onClick={handleDownload} disabled={downloading}>
              <DownloadIcon />
              {downloading ? 'Generating…' : 'Download PDF'}
            </button>
            <button className="hpg-btn hpg-btn--primary" onClick={handleSend} disabled={sending}>
              <SendIcon />
              {sending ? 'Sending…' : 'Send Offer'}
            </button>
          </div>
        </div>

        {/* ── RIGHT: live preview, styled like an actual letter ── */}
        <div className="hpg-preview-card">
          <div className="hpg-preview-header" style={{ background: company?.accentColor || '#12233D' }}>
            <div>
              <div className="hpg-preview-co">{company?.shortName || 'Select a company'}</div>
              <div className="hpg-preview-sub">{company?.fullName}</div>
            </div>
            <div className="hpg-preview-contact">
              {company?.website && <>{company.website}<br /></>}
              {company?.contactEmail && <>{company.contactEmail}<br /></>}
              {company?.contactPhone}
            </div>
          </div>

          <div className="hpg-letter">
            {isUnpaid ? (
              <>
                <p className="hpg-letter-title">Employment Offer Letter</p>
                <p>Dear <span className="hpg-accent" style={{ color: company?.accentColor }}>{form.candidateName || '<Candidate Name>'}</span>,</p>
                <p><strong>Start Date:</strong> <span className="hpg-accent" style={{ color: company?.accentColor }}>{fmtDate(form.startDate)}</span></p>

                <p><strong>Compensation and Benefits:</strong></p>
                <p>
                  You shall be participating in a full time unpaid internship as <span className="hpg-accent" style={{ color: company?.accentColor }}>{form.jobTitle || '<Job Title>'}</span>. Compensation will not be provided for your Services. You will be expected to work <strong>{form.hoursPerWeek || 25}</strong> hours per week.
                  {company?.einNumber && <> The Employer Identification Number (EIN) of {company?.shortName} is {company.einNumber}{company?.everifyNumber ? ` and E-Verify is ${company.everifyNumber}` : ''}.</>}
                </p>

                <p>For this position, your major responsibilities include but not limited to:</p>
                <ul>{respItems}</ul>

                <p>The following are the specific details of the position:</p>
                <ul>
                  <li>Work Hours: {form.workHours || '9:00 AM to 2:00 PM, Monday to Friday'}</li>
                  <li>Name of Supervisor: {company?.signatoryName || '[Signatory]'}</li>
                  <li>Job Title of Supervisor: {company?.signatoryTitle}</li>
                  {company?.contactPhone && <li>Phone: {company.contactPhone}</li>}
                  {company?.contactEmail && <li>Email ID: {company.contactEmail}</li>}
                </ul>

                <p><strong>Performance and Feedback</strong></p>
                <p>The Intern's performance will be reviewed periodically for learning, development, and completion of internship objectives. As this is an unpaid internship, no salary review or billing-rate adjustment applies.</p>

                <p><strong>Reports</strong></p>
                <p>The Employee will provide {company?.fullName || company?.shortName} with any reports that are deemed necessary, including periodic summaries of the Employee's work-related activities and accomplishments.</p>

                <p><strong>Confidentiality</strong></p>
                <p>The Employee recognizes and agrees that:</p>
                <p>In course of the employment by the Company, it may be necessary for the Employee to acquire information which may include, in whole or part, information concerning the Company's sales, sales volume, sales methods, sales proposals, customers and prospective customers, prospect list, sources of consultants, the Company's manuals formulae, Processes, methods, machines, compositions, ideas, improvements, inventions or other confidential or proprietary information belonging to the Company affairs.</p>
                <p>The confidential information is property of the Company. The use, misappropriation or disclosure of the confidential information would constitute a breach of trust and could cause irreparable injury, and Employee agrees to hold and safeguard the confidential information in trust of the Company, its successors and assigns and agrees that, without prior written consent of the Company during the Employee's employment with the Company or subsequent to the termination of the employment with or without cause.</p>
                <p>The confidential information, whether or not developed by the Employee, except as required in the performance of the Employee's duties to the Company.</p>

                <p><strong>Return of Materials</strong></p>
                <p>Upon termination of employment with the Company for any reason, including without limitation, termination by the company for the cause or without cause, the Employee shall promptly deliver to the Company all correspondence, drawings, manuals, letters, notes, notebooks, reports, prospect lists, flow charts, programs, proposals, and any documents concerning the Company's customers or suppliers and without limiting the foregoing, will promptly deliver to the Company and all other documents or material containing or constituting confidential material.</p>

                <p><strong>Non-Competition</strong></p>
                <p>The Intern is expected to avoid conflicts of interest during the internship. No post-internship non-compete restriction applies.</p>

                <p><strong>Non-Solicitation</strong></p>
                <p>The Intern agrees not to misuse the Company's confidential information or intentionally interfere with existing Company business relationships during the internship. No post-internship non-solicitation obligation is imposed.</p>
                <ol className="hpg-sublist" type="a">
                  <li>Solicit or induce, or attempt to solicit or induce, any employee or consultant of the Company to leave the Company for any reason whatsoever, or hire any employee or consultant of the Company.</li>
                  <li>Solicit or accept any business from a person, firm, or corporation that is a customer of the Company during the time the Employee is employed by the Company.</li>
                  <li>Solicit or accept any business from a person, firm, or corporation that is a prospective customer of the Company with whom the Employee had any dealings on the Company's behalf during the term of employment.</li>
                </ol>

                <p><strong>Termination</strong></p>
                <p>Employee needs to provide 30 days written notice for termination so that {company?.shortName} can manage replacement and other company specific hiring.</p>

                <p><strong>Remedies of Companies</strong></p>
                <p>In the event of a breach of the confidentiality obligations, the Company may seek remedies available under applicable law. No special damages, attorney-fee, or injunction provisions specific to unpaid interns are included.</p>

                <p><strong>Bonuses, Allowances, Commissions, Overtime, Extra Wages or Relocation</strong></p>
                <p>This internship is unpaid. Bonuses, commissions, overtime pay, relocation benefits, repayment obligations, and long-term employment commitments do not apply.</p>

                <p><strong>Authorization to Modify Restrictions</strong></p>
                <p>It is the intention of both the parties that the provisions of this Agreement shall be enforceable to the fullest extent permissible under applicable law. This Agreement shall be enforced in accordance with the laws of the State of DE. If any of its provisions are found to be inapplicable or unenforceability (or modification to confirm to such law) of any provision or provisions here shall not render unenforceable, or impair, the remainder thereof. If any provision or provisions which violate an applicable law or regulation shall be deemed void and unenforceable, and this Agreement shall be read, interpreted and enforced as though such provision is not contained herein. Pronoun references to male gender shall apply equally, when applicable, to females.</p>

                <p><strong>Tolling Period</strong></p>
                <p>This section is not applicable to this unpaid internship.</p>

                <p><strong>Company Violation Not a Defense</strong></p>
                <p>In an action by the Company to enforce this Agreement, and claims asserted by the Employee against the Company shall not constitute a defense to the Company's action.</p>

                <p><strong>Governing Law</strong></p>
                <p>This Agreement shall be governed by the construed and enforced in accordance with the laws of the State of Delaware.</p>

                <p><strong>Contingency Clause</strong></p>
                <p>This offer of employment is contingent upon the Employee's work authorization being approved by the U.S. Department of Homeland Security (DHS).</p>

                {/* Signature block, matching the internship template's order:
                    company name, then signatory name/title, then signature. */}
                <div className="hpg-sig-grid hpg-sig-grid--compact">
                  <div>
                    <p className="hpg-muted">{company?.shortName}</p>
                    <p><strong>{company?.signatoryName || '[Signatory]'}</strong></p>
                    <p className="hpg-muted">{company?.signatoryTitle}</p>
                    {company?.signatureImagePath && (
                      <img className="hpg-sig-img" src={company.signatureImagePath} alt={`${company?.signatoryName || 'Signatory'} signature`} />
                    )}
                    <div className="hpg-sig-line" />
                    <p className="hpg-sig-date">{fmtLong(form.offerDate)}</p>
                  </div>
                  <div>
                    <p className="hpg-muted">Accepted By (Print Full Name)</p>
                    <p className="hpg-accent" style={{ color: company?.accentColor }}>{form.candidateName || '<Candidate Name>'}</p>
                    <div className="hpg-sig-line" />
                    <p className="hpg-sig-date">{fmtLong(form.offerDate)}</p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="hpg-letter-date">{fmtLong(form.offerDate)}</p>
                <p>
                  <span className="hpg-accent" style={{ color: company?.accentColor }}>{form.candidateName || '<Candidate Name>'}</span>,<br />
                  <span className="hpg-muted">
                    {addressLines.map((l, i) => (
                      <Fragment key={i}>
                        {l}
                        {i < addressLines.length - 1 && <br />}
                      </Fragment>
                    ))}
                  </span>
                </p>
                <p>Dear <span className="hpg-accent" style={{ color: company?.accentColor }}>{firstName || '<Candidate Name>'}</span>,</p>
                <p>
                  It gives me great pleasure to extend to you an offer of employment with <strong>{company?.shortName || '<Company>'}</strong> (on a new assignment) as a{' '}
                  <span className="hpg-accent" style={{ color: company?.accentColor }}>{form.jobTitle || '<Job Title>'}</span>. This position will follow <strong>{form.workMode}</strong>, combining remote work with required in-office presence as per project and business needs.
                </p>
                <p>You will be initially assigned by {company?.shortName || '<Company>'} to a project scheduled to begin on <span className="hpg-accent" style={{ color: company?.accentColor }}>{fmtDate(form.startDate)}</span>.</p>
                <p><strong>The terms of the offer are as follows:</strong></p>
                <ul>
                  <li><span className="hpg-accent" style={{ color: company?.accentColor }}>${form.payRate || 'XX.XX'} USD per hour.</span> {company?.shortName} payroll is run on a monthly basis (please refer to the payroll calendar for details).</li>
                  <li>Details as per the attached offer acceptance letter, if applicable.</li>
                  <li>Approved signed timesheets should be submitted every week.</li>
                  <li>
                    {company?.shortName} will reimburse all travel expenses subject to the following conditions:
                    {/* Nested numbered sub-list — matches the target's 1/2/3 style
                        instead of cramming "(1)...(2)...(3)" into one bullet. */}
                    <ol className="hpg-sublist">
                      <li>The required approval in writing is obtained.</li>
                      <li>You are responsible for understanding and following the travel policy as defined in the provided documentation.</li>
                      <li>Expenses and supporting receipts must be submitted in the prescribed format.</li>
                    </ol>
                  </li>
                </ul>
                <p>{company?.probationClause}</p>
                <p><strong>The responsibilities of the <span className="hpg-accent" style={{ color: company?.accentColor }}>{form.jobTitle || '<Job Title>'}</span> include:</strong></p>
                <ul>{respItems}</ul>
                <p>This offer is contingent upon your acceptance, signing, and completion of the following:</p>
                <ul>
                  <li>{company?.shortName} Employment Agreement</li>
                  <li>Acknowledgment of {company?.shortName} Employee Handbook</li>
                  <li>Acceptance of all client conditions and documents related to worksite rules, confidentiality, and other requirements</li>
                </ul>
                <p>{company?.benefitsClause}</p>
                <p>
                  In consideration of your employment, you agree to protect all confidential and proprietary information of the company and assign ownership of any inventions, discoveries, or improvements created during your employment to the company. The compensation and benefits described in this letter are confidential and should not be shared outside your representative and/or immediate family.
                </p>
                <p>This offer of employment is contingent upon:</p>
                <ul>
                  <li>Beginning your employment with the company no later than the specified date.</li>
                  <li>Clearing the required drug test and/or background check.</li>
                  <li>Final approval from the client permitting you to commence the project at its site.</li>
                </ul>
                <p>This offer constitutes at-will employment and does not guarantee employment for any specific term. {company?.shortName} reserves the right to withdraw this offer in the event of client cancellation or non-approval.</p>
                <p>We look forward to welcoming you to the {company?.shortName} team. Should you have any questions, please contact me directly at <strong>{company?.contactPhone}</strong>.</p>

                {/* Signature block — both sides get an underline; only the
                    manager's side also shows a date beneath it. */}
                <div className="hpg-sig-grid hpg-sig-grid--compact">
                  <div>
                    <p><strong>{company?.signatoryName || '[Signatory]'}</strong></p>
                    <p className="hpg-muted">{company?.signatoryTitle ? `${company.signatoryTitle},` : ''}</p>
                    <p className="hpg-muted">{company?.fullName}</p>
                    {company?.signatureImagePath && (
                      <img className="hpg-sig-img" src={company.signatureImagePath} alt={`${company?.signatoryName || 'Signatory'} signature`} />
                    )}
                    <div className="hpg-sig-line" />
                    <p className="hpg-sig-date">{fmtLong(form.offerDate)}</p>
                  </div>
                  <div>
                    <p className="hpg-muted">Accepted By (Print Full Name)</p>
                    <p className="hpg-accent" style={{ color: company?.accentColor }}>{form.candidateName || '<Candidate Name>'}</p>
                    <div className="hpg-sig-line" />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="hpg-preview-footer">
            <span>{company?.fullName} {company?.contactPhone ? `| ${company.contactPhone}` : ''}</span>
            <span>{company?.addressLine}</span>
          </div>
        </div>
      </div>

      {toast && (
        <div className={`hpg-toast hpg-toast--${toast.type === 'ok' ? 'ok' : toast.type === 'err' ? 'err' : 'info'}`} role="status">
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function DownloadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3v12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 19h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M21 3 3 10.5l7 2.5 2.5 7L21 3Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12.5 13.5 21 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <line x1="12" y1="4" x2="12" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <line x1="5" y1="5" x2="19" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="19" y1="5" x2="5" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
